import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { animate, stagger, createDrawable } from 'animejs';
import './Hero.css';

const SLIDE_IDS = ['brand', 'store', 'services'];
const ROTATE_MS = 7000;

const CAROUSEL_BG_IMAGES = {
  brand: '/images/hero-carousel-marca-ecosistema.png',
  store: '/images/hero-carousel-tienda-proposito.png'
};

function HeroTriangleXRaySvg() {
  return (
    <div className="hero-slide-xray-overlay" aria-hidden="true">
      <svg className="hero-slide-xray-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
        <polygon
          className="hero-slide-xray-triangle"
          points="200,80 320,320 80,320"
          fill="none"
          stroke="var(--javiland-hueso)"
          strokeWidth="1.5"
        />
        <g className="hero-slide-xray-rays">
          {[...Array(12)].map((_, i) => {
            const angle = (i * 30) * (Math.PI / 180);
            const x1 = 200 + Math.cos(angle) * 60;
            const y1 = 200 + Math.sin(angle) * 60;
            const x2 = 200 + Math.cos(angle) * 220;
            const y2 = 200 + Math.sin(angle) * 220;
            return (
              <line
                key={`ray-${String(i)}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="var(--javiland-hueso)"
                strokeWidth="0.8"
              />
            );
          })}
        </g>
        <g className="hero-slide-xray-diagonals">
          <line x1="80" y1="320" x2="200" y2="80" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
          <line x1="320" y1="320" x2="200" y2="80" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
          <line x1="80" y1="320" x2="320" y2="320" stroke="var(--javiland-hueso)" strokeWidth="0.6" />
        </g>
      </svg>
    </div>
  );
}

/** Divide un string en spans animables por palabra */
function WordSpans({ text }) {
  const words = text.split(' ');
  return (
    <>
      {words.map((word, i) => (
        <React.Fragment key={i}>
          <span className="hero-word">{word}</span>
          {i < words.length - 1 && ' '}
        </React.Fragment>
      ))}
    </>
  );
}

const Hero = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const animTimers = useRef([]);

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const go = useCallback((dir) => {
    setActive((i) => {
      const n = SLIDE_IDS.length;
      return (i + dir + n) % n;
    });
  }, []);

  /* Rotación automática */
  useEffect(() => {
    if (paused) return undefined;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setActive((i) => (i + 1) % SLIDE_IDS.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [paused]);

  /* ── Animaciones Anime.js cuando cambia el slide activo ── */
  useEffect(() => {
    animTimers.current.forEach(clearTimeout);
    animTimers.current = [];

    const slide = document.querySelector('.hero-carousel-slide.is-active');
    if (!slide) return;

    const words  = slide.querySelectorAll('.hero-word');
    const sub    = slide.querySelector('.hero-carousel-subtitle');
    const cta    = slide.querySelector('.hero-carousel-cta');

    /* Estado inicial: todo invisible y desplazado */
    words.forEach((w) => {
      w.style.opacity   = '0';
      w.style.transform = 'translateY(22px)';
    });
    if (sub) { sub.style.opacity = '0'; sub.style.transform = 'translateY(16px)'; }
    if (cta) { cta.style.opacity = '0'; cta.style.transform = 'translateY(12px)'; }

    /* Pequeño delay para que el slide termine de aparecer */
    const t0 = setTimeout(() => {
      if (words.length) {
        animate(words, {
          opacity : [0, 1],
          translateY: [22, 0],
          delay   : stagger(65),
          duration: 480,
          ease    : 'outExpo',
        });
      }

      const afterWords = words.length * 65 + 120;

      if (sub) {
        const ts = setTimeout(() => {
          animate(sub, {
            opacity   : [0, 1],
            translateY: [16, 0],
            duration  : 520,
            ease      : 'outExpo',
          });
        }, afterWords);
        animTimers.current.push(ts);
      }

      if (cta) {
        const tc = setTimeout(() => {
          animate(cta, {
            opacity   : [0, 1],
            translateY: [12, 0],
            duration  : 440,
            ease      : 'outExpo',
          });
        }, afterWords + 160);
        animTimers.current.push(tc);
      }
    }, 120);

    animTimers.current.push(t0);

    /* ── Triángulo: se dibuja solo cuando es el slide de marca ── */
    if (active === 0) {
      const tSvg = setTimeout(() => {
        const triangle = document.querySelector('.hero-carousel-slide.is-active .hero-slide-xray-triangle');
        const rays     = document.querySelectorAll('.hero-carousel-slide.is-active .hero-slide-xray-rays line');

        if (triangle) {
          try {
            const drawable = createDrawable(triangle);
            animate(drawable, {
              draw    : ['0 0', '0 1'],
              duration: 1600,
              ease    : 'inOutCubic',
            });
          } catch (_) { /* degradación graceful */ }
        }

        if (rays.length) {
          try {
            animate(createDrawable(rays), {
              draw    : ['0 0', '0 1'],
              delay   : stagger(55, { start: 400 }),
              duration: 500,
              ease    : 'outExpo',
            });
          } catch (_) { /* degradación graceful */ }
        }
      }, 250);
      animTimers.current.push(tSvg);
    }

    return () => {
      animTimers.current.forEach(clearTimeout);
    };
  }, [active]);

  const slideMeta = [
    { variant: 'triangle', path: '/', num: 1 },
    { variant: 'photo', imageSrc: CAROUSEL_BG_IMAGES.store, path: '/tienda', num: 2 },
    { variant: 'photo', imageSrc: CAROUSEL_BG_IMAGES.brand, path: '/servicios', num: 3 }
  ];

  return (
    <section className="hero hero-fou" id="inicio">
      <div className="hero-banners">
        <div
          className="hero-carousel hero-carousel--fullwidth"
          role="region"
          aria-roledescription={t('hero.carousel.ariaCarousel')}
          aria-label={t('hero.carousel.ariaLabel')}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
          }}
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0].clientX;
            setPaused(true);
          }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
            touchStartX.current = null;
            setPaused(false);
          }}
        >
          {slideMeta.map((slide, index) => (
            <div
              key={SLIDE_IDS[index]}
              className={`hero-carousel-slide ${active === index ? 'is-active' : ''} ${slide.variant === 'photo' ? 'hero-carousel-slide--photo' : ''} ${slide.variant === 'triangle' ? 'hero-carousel-slide--triangle' : ''}`}
              aria-hidden={active !== index}
            >
              <div
                className={`hero-slide-bg ${slide.variant === 'photo' ? 'hero-slide-bg--photo' : 'hero-slide-bg--triangle'}`}
                style={
                  slide.variant === 'photo'
                    ? { backgroundImage: `url(${slide.imageSrc})` }
                    : undefined
                }
                aria-hidden="true"
              >
                {slide.variant === 'triangle' && <HeroTriangleXRaySvg />}
              </div>
              <div className="hero-carousel-content">
                <h2 className="hero-carousel-title">
                  <WordSpans text={t(`hero.carousel.slide${slide.num}.title`)} />
                </h2>
                <p className="hero-carousel-subtitle">{t(`hero.carousel.slide${slide.num}.subtitle`)}</p>
                <a
                  href={slide.path}
                  className="hero-carousel-cta"
                  onClick={(e) => {
                    e.preventDefault();
                    navigateToPage(slide.path);
                  }}
                >
                  {t(`hero.carousel.slide${slide.num}.cta`)}
                </a>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="hero-carousel-control hero-carousel-prev"
            aria-label={t('hero.carousel.prev')}
            onClick={() => go(-1)}
          >
            <i className="fas fa-chevron-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="hero-carousel-control hero-carousel-next"
            aria-label={t('hero.carousel.next')}
            onClick={() => go(1)}
          >
            <i className="fas fa-chevron-right" aria-hidden="true" />
          </button>
          <div className="hero-carousel-indicators" role="tablist" aria-label={t('hero.carousel.dotsLabel')}>
            {SLIDE_IDS.map((_, i) => (
              <button
                key={SLIDE_IDS[i]}
                type="button"
                role="tab"
                aria-selected={active === i}
                aria-label={t('hero.carousel.goTo', { n: i + 1 })}
                className={`hero-carousel-indicator ${active === i ? 'is-active' : ''}`}
                onClick={() => setActive(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );

};

export default Hero;
